import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { materialApi } from "@/lib/api/services/material.api";
import { getApiErrorMessage } from "@/lib/api/api";
import type { Allergen } from "@/types/material";

interface AllergenSettingsCardProps {
  canManage: boolean;
}

/**
 * Settings → Configuration → Allergens (T-1006).
 *
 * The built-ins are shown but not editable: the Top-9 are a legal list, not a
 * preference, and a kitchen that could rename "Tree nuts" would be able to make
 * a label say something untrue. What a tenant may add is everything the law
 * does not name — sesame paste, a house spice blend someone reacts to.
 *
 * There is no delete. An allergen is referenced by the ingredients carrying it,
 * and removing one would quietly change what those labels claim.
 */
export function AllergenSettingsCard({ canManage }: AllergenSettingsCardProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [entry, setEntry] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["allergens"],
    queryFn: () => materialApi.getAllergens(),
  });

  const add = useMutation({
    mutationFn: (name: string) => materialApi.createAllergen(name),
    onSuccess: () => {
      setEntry("");
      queryClient.invalidateQueries({ queryKey: ["allergens"] });
      toast.success(t`Allergen added`);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to add the allergen`)),
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Allergens`}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Allergens`}</CardTitle>
          <CardDescription>{t`The allergen list could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const isCustom = (allergen: Allergen) => allergen.organizationId != null;
  const custom = data.filter(isCustom);
  const builtIn = data.filter((a) => !isCustom(a));

  const submit = () => {
    const name = entry.trim();
    if (name === "") return;
    if (data.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t`"${name}" is already on the allergen list`);
      return;
    }
    add.mutate(name);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Allergens`}</CardTitle>
        <CardDescription>
          {t`The declarable allergens every kitchen carries, plus any this one adds. Custom allergens can be put on an ingredient like any other.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Standard`}</p>
          <ul className="flex flex-wrap gap-2">
            {builtIn.map((allergen) => (
              <li
                key={allergen.id}
                className="rounded-full border border-(--color-border) bg-(--color-surface-muted) px-3 py-1 text-sm text-(--color-text-secondary)"
              >
                {allergen.name}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Added by this kitchen`}</p>
          {custom.length === 0 ? (
            <p className="text-sm text-(--color-text-secondary)">
              {t`None yet. Add one for anything the standard list does not name.`}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {custom.map((allergen) => (
                <li
                  key={allergen.id}
                  className="rounded-full border border-(--color-border) bg-(--color-surface-muted) px-3 py-1 text-sm"
                >
                  {allergen.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canManage && (
          <div className="flex items-center gap-2">
            <Input
              aria-label={t`Add an allergen`}
              value={entry}
              maxLength={60}
              disabled={add.isPending}
              placeholder={t`e.g. Sesame paste`}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
              className="max-w-xs"
            />
            <Button
              onClick={submit}
              disabled={entry.trim() === "" || add.isPending}
              className="gap-1.5"
            >
              {add.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Plus className="size-3.5" />
              )}
              {t`Add`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
